'use client';
import React from 'react';

import { SessionContext } from '@benzinga/session-context';
import { PermissionsManager, PermissionsManagerEvent } from '@benzinga/permission-manager';
import Hooks from '@benzinga/hooks';

import { checkDeviceType } from '@benzinga/device-utils';

import { useIsUserLoggedIn } from './useIsUserLoggedIn';
import { useDetectIncognitoMode } from './useDetectIncognitoMode';
import { usePermission } from './usePermission';
import { isUrlHasPartnerReferrerUTMSource, sophiManager } from '@benzinga/ads-utils';
import { getValueFromCookie } from '@benzinga/utils';

type PaywallType = 'soft' | 'soft-hard' | 'hard';
type PaywallStyle = 'account-creation' | 'edge-hard' | 'edge-soft' | 'sophi';

const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));
// const localStorageKey = 'pwdata';

export interface IsUserPaywalledReturn {
  active: boolean;
  hasAccess: boolean;
  isLoading: boolean;
  isPaywalled: boolean;
  paywallStyle: PaywallStyle;
  enable: () => void;
  disable: () => void;
}

/**
 * Hook to check whether user is paywalled
 */

export const useIsUserPaywalled = (
  action?: string,
  resource?: string,
  paywallType: PaywallType = 'soft',
  disableArtificialLoading?: boolean,
): IsUserPaywalledReturn => {
  const session = React.useContext(SessionContext);
  const permissionsManager = session.getManager(PermissionsManager);

  const hasAccount = getValueFromCookie('bz_access_type');
  const [isPaywalled, setIsPaywalled] = React.useState(false); // default to paywall off
  const [paywallStyle, setPaywallStyle] = React.useState<PaywallStyle>('edge-hard'); // default to hard paywall
  const [isSophiReady, setIsSophiReady] = React.useState(sophiManager.isLoaded()); // track Sophi ready state
  const isBot = React.useMemo(() => checkDeviceType().isBot(), []); // check if user is bot
  const isEditor = usePermission('editorial/edit'); // check if user is an editor
  const isBenzingaContributor = usePermission('cms/role'); // check if user is a benzinga contributor
  const isLoggedIn = useIsUserLoggedIn(); // check if user is logged in
  const isIncognito = useDetectIncognitoMode(); // check if user is in incognito mode
  const [hasAccess, setHasAccess] = React.useState(hasAccount !== 'anonymous' ? true : false);
  const [isLoading, setIsLoading] = React.useState(disableArtificialLoading ? false : true);

  // use permission hook to check if user has access, only check if permissions are loaded
  // using usePermission hook will return false if permissions are not loaded, so not ideal to use here
  Hooks.useSubscriber(permissionsManager, (event: PermissionsManagerEvent) => {
    switch (event.type) {
      case 'permission_changed':
        if (action) {
          const access = permissionsManager.hasAccess(action, resource);
          if (!access.err) {
            setHasAccess(access.ok);
          }
        }
        break;
    }
  });

  const isPartnerUrl = (path?: string) => {
    // Check if it starts with '/partner'
    return path && path.startsWith('/partner');
  };

  const isEmailReferral = (search?: string) => {
    const params = new URLSearchParams(search);
    const isSummaryEmail = params.get('utm_source') === 'summary-email';
    const isNewsletter = params.get('utm_medium') === 'newsletter';
    const isArticleShare = params.get('utm_source') === 'articleShare';
    const isFlipboardPromo = params.get('utm_source') === 'd068ae2b';
    return isSummaryEmail || isNewsletter || isArticleShare || isFlipboardPromo;
  };

  const forcePaywall = (search?: string) => {
    if (!search) return false;

    const params = new URLSearchParams(search);
    const paywallStyle = params.get('paywall_style');
    const campaign = params.get('utm_campaign');

    if (paywallStyle === 'account-creation') {
      setPaywallStyle('account-creation');
    } else if (paywallStyle !== 'edge-hard') {
      setPaywallStyle('sophi');
    }
    return campaign === 'forcepaywall';
  };

  React.useEffect(() => {
    if (!disableArtificialLoading) {
      setIsLoading(true);
      delay(3000).then(() => {
        setIsLoading(false);
      });
    }
  }, [disableArtificialLoading]);

  React.useEffect(() => {
    if (sophiManager.isLoaded()) {
      setIsSophiReady(true);
    } else {
      sophiManager.onLoad(() => {
        setIsSophiReady(true);
      });
    }
  }, []);

  React.useEffect(() => {
    if (!action) {
      setHasAccess(isLoggedIn); // if no action then determine access based on login status otherwise based on permission
      return;
    }
  }, [action, isLoggedIn]);

  React.useEffect(() => {
    const getPermissions = async action => {
      const permissions = await permissionsManager.getPermissions();
      if (permissions.ok) {
        const access = permissionsManager.hasAccess(action, resource);
        if (!access.err) {
          setHasAccess(access.ok);
        }
      }
    };

    if (isLoggedIn && action) {
      getPermissions(action);
    }
  }, [action, isLoggedIn, permissionsManager, resource]);

  React.useEffect(() => {
    const showPaywall = forcePaywall(window?.location?.search);
    if (showPaywall) {
      setIsPaywalled(true);
      return;
    }

    // do not paywall
    if (isEditor || isBenzingaContributor || isBot) {
      setIsPaywalled(false);
      return;
    }

    // do not paywall
    if (
      isPartnerUrl(window?.location?.pathname) ||
      isUrlHasPartnerReferrerUTMSource(window?.location?.href) ||
      isEmailReferral(window?.location?.search)
    ) {
      setIsPaywalled(false);
      return;
    }

    // For unlimited-articles resource, wait for Sophi to be ready
    if (resource === 'unlimited-articles' && !isSophiReady) {
      console.log('[Paywall] Waiting for Sophi to be ready before making paywall decision');
      return;
    }

    const getShowPaywallDecision = async (): Promise<boolean | null> => {
      const userType = isLoggedIn ? 'registered' : 'anonymous';
      const decision = await sophiManager.getDecision(userType);

      if (decision === null) return null;
      if (decision?.outcome?.wallType === 'regwall') {
        setPaywallStyle('account-creation');
      }
      if (decision?.outcome?.wallVisibility === 'always') return true;
      if (decision?.outcome?.wallVisibility === 'never') return false;
      return false;
    };

    console.log('[Paywall] Making paywall decision - Sophi ready:', isSophiReady, 'Resource:', resource);
    if (resource === 'unlimited-articles') {
      setPaywallStyle('sophi');
      if (!hasAccess && paywallType === 'hard') {
        console.log('[Paywall] Showing hard paywall for locked articles');
        setIsPaywalled(true);
        return;
      }

      (async () => {
        const decision = await getShowPaywallDecision();

        if (decision === null) {
          console.log('[Paywall] No decision from Sophi, falling through to default no paywall');
        } else {
          // use Sophi decision
          if (decision) {
            setIsPaywalled(true);
          } else {
            setIsPaywalled(false);
          }
        }
        return; // Exit out for async function
      })();
    } else {
      // immediate paywall based on access
      if (!hasAccess && paywallType === 'hard') {
        console.log('[Paywall] Showing hard paywall for locked tools');
        setIsPaywalled(true);
        return;
      }
      setIsPaywalled(false);
    }
  }, [hasAccess, isLoggedIn, isEditor, isBenzingaContributor, isBot, paywallType, isIncognito, resource, isSophiReady]);

  const handleDisablePaywall = () => {
    setIsPaywalled(false);
  };

  const handleEnablePaywall = () => {
    setIsPaywalled(true);
  };

  return {
    active: isPaywalled && !hasAccess && !isLoading,
    disable: handleDisablePaywall,
    enable: handleEnablePaywall,
    hasAccess: hasAccess,
    isLoading: isLoading,
    isPaywalled: isPaywalled,
    paywallStyle: paywallStyle,
  };
};
